/**
 * Admin Panel Layout
 * Dark sidebar with profile dropdown (top-right) for logout
 * Dynamic panel name from settings
 */
import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Key,
  ListVideo,
  MonitorSmartphone,
  Palette,
  Bell,
  Download,
  FileText,
  Database,
  Tv,
  Play,
  Users,
  UserCheck,
  Globe as GlobeIcon,
  CreditCard,
  Tag,
  Wallet,
  Receipt,
  Settings as SettingsIcon,
  LogOut,
  ChevronDown,
  User as UserIcon,
  KeyRound,
  Lock,
  Coins,
  Infinity as InfinityIcon,
  ArrowLeftCircle,
  ShieldAlert,
  Store,
  ShieldCheck,
  HardDrive,
} from "lucide-react";
import { ADMIN } from "./lib/testIds";
import { Toaster } from "@/components/ui/toaster";
import { useHealthStatus } from "./lib/useHealthStatus";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "./lib/AuthContext";
import { getSettings, changeMyPassword, setMyPlaylistPassword } from "./lib/api";
import { useToast } from "@/hooks/use-toast";
import { APP_VERSION } from "../player/lib/version";

// All possible nav items (filtered by role)
const allNavItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testId: ADMIN.navDashboard },
  { to: "/admin/keys", label: "Activation Keys", icon: Key, testId: ADMIN.navKeys },
  { to: "/admin/playlists", label: "Playlists", icon: ListVideo, testId: ADMIN.navPlaylists },
  { to: "/admin/metadata-snapshot", label: "Cover & Snapshot", icon: Database, testId: "admin-nav-metadata-snapshot", adminOnly: true },
  { to: "/admin/devices", label: "Devices", icon: MonitorSmartphone, testId: ADMIN.navDevices },
  { to: "/admin/clients", label: "Clients", icon: UserCheck, testId: "admin-nav-clients" },
  { to: "/admin/website", label: "Website", icon: GlobeIcon, testId: "admin-nav-website", adminOnly: true },
  { to: "/admin/paygate", label: "Paygate", icon: CreditCard, testId: "admin-nav-paygate", adminOnly: true },
  { to: "/admin/pending-payments", label: "Pending payments", icon: Receipt, testId: "admin-nav-pending-payments", adminOnly: true },
  { to: "/admin/price-plans", label: "Price plans", icon: Tag, testId: "admin-nav-price-plans", adminOnly: true },
  { to: "/admin/buy-credits", label: "Buy credits", icon: Wallet, testId: "admin-nav-buy-credits", adminOnly: false },
  { to: "/admin/notifications", label: "Notifications", icon: Bell, testId: ADMIN.navNotifications },
  { to: "/admin/branding", label: "Branding", icon: Palette, testId: ADMIN.navBranding, adminOnly: true },
  { to: "/admin/updates", label: "App Updates", icon: Download, testId: ADMIN.navUpdates, adminOnly: true },
  { to: "/admin/app-store-readiness", label: "App Store Readiness", icon: Store, testId: "admin-nav-app-store-readiness", adminOnly: true },
  { to: "/admin/logs", label: "Debug Logs", icon: FileText, testId: "admin-nav-logs", adminOnly: true },
  { to: "/admin/missing-metadata", label: "Missing Metadata", icon: Database, testId: "admin-nav-missing-metadata", adminOnly: true },
  { to: "/admin/profile-cache", label: "Profile Cache", icon: Users, testId: "admin-nav-profile-cache", adminOnly: true },
  { to: "/admin/users", label: "Users", icon: Users, testId: "admin-nav-users", adminOnly: true },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testId: "admin-nav-settings", adminOnly: true },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, refreshUser, stopImpersonation, isImpersonating } = useAuth();
  const { toast } = useToast();

  const [panelName, setPanelName] = useState("ZA Player Pro Admin");
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  // v1.4.38 — Playlist Lock dialog. Resellers open this from the
  // profile dropdown to set/change/clear the password that gates
  // their Playlists table view/edit.
  const [lockOpen, setLockOpen] = useState(false);
  const [lockPw, setLockPw] = useState("");
  const [savingLock, setSavingLock] = useState(false);

  useEffect(() => {
    getSettings()
      .then((s) => setPanelName(s.panel_name || "ZA Player Pro Admin"))
      .catch(() => {});
  }, []);

  // Refresh user every 30s to keep credits balance fresh
  useEffect(() => {
    if (user?.role !== "reseller") return;
    const id = setInterval(() => {
      refreshUser().catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [user?.role, refreshUser]);

  const handleReturnToAdmin = async () => {
    const ok = await stopImpersonation();
    if (ok) {
      toast({ title: "Returned to admin account" });
      navigate("/admin", { replace: true });
    } else {
      toast({ title: "Session expired, please log in again", variant: "destructive" });
      navigate("/admin/login", { replace: true });
    }
  };

  const isAdmin = user?.role === "admin";
  const navItems = allNavItems.filter((item) => !item.adminOnly || isAdmin);

  const currentPage = navItems.find((n) =>
    n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)
  );

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const handleChangePassword = async () => {
    if (!newPw || newPw.length < 3) {
      toast({ title: "Password too short (min 3)", variant: "destructive" });
      return;
    }
    setSavingPw(true);
    try {
      await changeMyPassword(newPw);
      toast({ title: "Password changed" });
      setPwOpen(false);
      setNewPw("");
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSavingPw(false);
    }
  };

  // v1.4.38 — Save/clear the playlist lock password.
  const handleSaveLock = async (clear = false) => {
    if (!clear && (!lockPw || lockPw.length < 3)) {
      toast({ title: "Playlist password too short (min 3)", variant: "destructive" });
      return;
    }
    setSavingLock(true);
    try {
      await setMyPlaylistPassword(clear ? "" : lockPw);
      toast({
        title: clear ? "Playlist lock removed" : "Playlist lock updated",
        description: clear
          ? "Your playlists are no longer hidden."
          : "M3U / EPG / B2C URLs will be masked in the playlists table.",
      });
      setLockOpen(false);
      setLockPw("");
      // Refresh the user so has_playlist_password flips immediately.
      refreshUser().catch(() => {});
    } catch (e) {
      toast({
        title: "Error",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setSavingLock(false);
    }
  };

  const userInitial = (user?.display_name || user?.username || "U")[0].toUpperCase();

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900">
      {/* Sidebar */}
      <aside
        data-testid={ADMIN.sidebar}
        className="w-64 shrink-0 bg-slate-900 text-slate-200 flex flex-col"
      >
        <div className="px-6 py-5 border-b border-slate-800 flex items-center gap-2">
          <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-pink-600 rounded-lg flex items-center justify-center">
            <Tv className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="font-bold text-white text-base leading-tight truncate"
              title={panelName}
              data-testid="sidebar-panel-name"
            >
              {panelName}
            </div>
            <div className="text-[11px] text-slate-400 leading-tight" data-testid="sidebar-console-label">
              {isAdmin ? "Admin Console" : "Reseller Console"}
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-slate-800 text-white"
                      : "text-slate-400 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
          {/* v1.4.7 — Web Player launcher. Admin-only: resellers never
              need direct player access from the panel. */}
          {isAdmin && (
            <a
              href="/admin/webplayer"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-nav-web-player"
              className="mt-4 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-white bg-gradient-to-r from-fuchsia-600 to-orange-500 hover:from-fuchsia-500 hover:to-orange-400 transition-colors shadow-lg shadow-fuchsia-900/30"
            >
              <Play className="w-4 h-4 fill-current" />
              Open Web Player
            </a>
          )}
        </nav>

        <div className="px-6 py-4 border-t border-slate-800 text-xs text-slate-500">
          <SidebarHealthDots />
          Android release
          <br />
          <span className="text-slate-600">Direct APK · Dropbox OTA</span>
          <div
            data-testid="admin-panel-version"
            className="mt-2 pt-2 border-t border-slate-800/60 text-[10px] text-slate-600 font-mono tracking-wide"
          >
            v{APP_VERSION}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* Impersonation banner */}
        {isImpersonating && (
          <div
            data-testid="impersonation-banner"
            className="bg-amber-500 text-amber-950 px-6 py-2 flex items-center justify-between text-sm font-medium shadow-sm"
          >
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" />
              <span>
                You are signed in as{" "}
                <span className="font-bold">{user?.username}</span>. All actions are taken
                on this reseller&apos;s behalf.
              </span>
            </div>
            <button
              data-testid="return-to-admin"
              onClick={handleReturnToAdmin}
              className="flex items-center gap-1.5 bg-amber-950 text-amber-100 px-3 py-1 rounded-md hover:bg-amber-900 transition-colors"
            >
              <ArrowLeftCircle className="w-4 h-4" />
              Return to Admin
            </button>
          </div>
        )}

        <header className="bg-white border-b border-slate-200 px-8 py-3.5 sticky top-0 z-10 flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-slate-900">
            {currentPage?.label || "Admin"}
          </h1>

          <div className="flex items-center gap-3">
            {/* Credits pill (resellers only) */}
            {user?.role === "reseller" && (
              <div
                data-testid="credits-pill"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border ${
                  user.unlimited_credits
                    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                    : (user.credits ?? 0) <= 0
                    ? "bg-red-50 border-red-200 text-red-700"
                    : (user.credits ?? 0) < 5
                    ? "bg-amber-50 border-amber-200 text-amber-700"
                    : "bg-orange-50 border-orange-200 text-orange-700"
                }`}
                title="Available credits (1 credit = 1 activation key)"
              >
                <Coins className="w-3.5 h-3.5" />
                {user.unlimited_credits ? (
                  <>
                    <InfinityIcon className="w-3.5 h-3.5" />
                    <span>Unlimited</span>
                  </>
                ) : (
                  <span data-testid="credits-value">{user.credits ?? 0} credits</span>
                )}
              </div>
            )}

            {/* Profile dropdown - top right */}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="profile-menu-trigger"
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-pink-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {userInitial}
                </div>
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium text-slate-900 leading-tight">
                    {user?.display_name || user?.username}
                  </div>
                  <div className="text-[11px] text-slate-500 capitalize leading-tight">
                    {user?.role}
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex items-center gap-2">
                  <UserIcon className="w-4 h-4" />
                  <span className="font-mono text-xs">{user?.username}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="profile-change-password"
                onClick={() => setPwOpen(true)}
                className="cursor-pointer"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Change password
              </DropdownMenuItem>
              {/* v1.4.67 — Playlist Lock is now available to admins too
                  (previously reseller-only). Any user with a
                  `playlist_password_hash` on their doc gets the
                  masked-URL gate on their own playlist admin table. */}
              {(user?.role === "reseller" || user?.role === "admin") && (
                <DropdownMenuItem
                  data-testid="profile-playlist-lock"
                  onClick={() => setLockOpen(true)}
                  className="cursor-pointer"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {user?.has_playlist_password ? "Change playlist lock" : "Set playlist lock"}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="profile-logout"
                onClick={handleLogout}
                className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>
        <div className="p-8">
          <Outlet />
        </div>
        {/* v1.3.82 — Admin panel version footer. Visible on every page
            so support can quickly ask users which version they're on. */}
        <footer
          data-testid="admin-panel-footer-version"
          className="px-8 py-3 border-t border-slate-200 bg-slate-50 text-xs text-slate-500 flex items-center justify-between"
        >
          <span>ZA Player Pro Admin</span>
          <span className="font-mono">v{APP_VERSION}</span>
        </footer>
      </main>

      <Toaster />

      {/* Change my password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Your Password</DialogTitle>
            <DialogDescription>Enter a new password for your account.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>New Password</Label>
            <Input
              data-testid="my-password-input"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="my-password-save"
              onClick={handleChangePassword}
              disabled={savingPw}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {savingPw ? "Saving..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v1.4.38 — Playlist Lock dialog (resellers only) */}
      <Dialog open={lockOpen} onOpenChange={(o) => { setLockOpen(o); if (!o) setLockPw(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Playlist Lock
            </DialogTitle>
            <DialogDescription>
              When set, every playlist in your table will show a lock icon and mask its M3U /
              EPG / B2C URLs. You (or anyone using this account) will be asked for this password
              before viewing or editing a playlist. Leaving it empty removes the lock.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{user?.has_playlist_password ? "New playlist password" : "Playlist password"}</Label>
              <Input
                data-testid="playlist-lock-input"
                type="password"
                value={lockPw}
                onChange={(e) => setLockPw(e.target.value)}
                placeholder="min 3 characters"
                autoFocus
              />
            </div>
            {user?.has_playlist_password && (
              <div className="text-xs text-slate-500">
                A playlist lock is currently active. Enter a new password to change it, or use
                "Remove lock" to clear it.
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <div>
              {user?.has_playlist_password && (
                <Button
                  data-testid="playlist-lock-remove"
                  variant="outline"
                  onClick={() => handleSaveLock(true)}
                  disabled={savingLock}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                >
                  Remove lock
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setLockOpen(false)}>
                Cancel
              </Button>
              <Button
                data-testid="playlist-lock-save"
                onClick={() => handleSaveLock(false)}
                disabled={savingLock}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {savingLock ? "Saving..." : user?.has_playlist_password ? "Update" : "Set lock"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ------------------------------------------------------------------
// SidebarHealthDots — v1.4.28-h
// ------------------------------------------------------------------
// Two tiny colored dots pinned to the bottom of the admin sidebar.
// Green when the corresponding daemon reports "healthy", red on
// failure, amber for warn/stale, slate for loading/unknown.
//
// Clicking a dot navigates to the App Store Readiness page which
// hosts the full-size chips with detailed metadata. On hover, the
// browser-native `title` attribute surfaces a one-line summary so
// the admin doesn't have to click through unless they want to.
// ------------------------------------------------------------------
function SidebarHealthDots() {
  const { smoke, smokeError, storage, storageError } = useHealthStatus();

  const dotColor = (state, isError) => {
    if (isError) return "bg-slate-500";
    switch (state) {
      case "pass":     return "bg-emerald-400";
      case "fail":     return "bg-red-500";
      case "warn":     return "bg-amber-400";
      case "critical": return "bg-red-500";
      case "stale":    return "bg-amber-400";
      default:         return "bg-slate-500";
    }
  };

  const smokeState = smokeError ? "error" : smoke?.state || "loading";
  const storageState = storageError ? "error" : storage?.state || "loading";

  const smokeTitle = smokeError
    ? "Nightly regression — cannot reach /api/admin/health/smoke"
    : smoke
      ? `Nightly regression: ${smoke.state}\n${smoke.summary || "(no summary)"}`
      : "Loading nightly regression status…";

  const storageTitle = storageError
    ? "Storage — cannot reach /api/admin/health/storage"
    : storage
      ? `Storage: ${storage.state}\n${storage.disk_pct}% used · ${storage.disk_free_mb} MB free`
      : "Loading storage health…";

  return (
    <div className="mb-3 flex items-center gap-3 text-[10px] text-slate-500">
      <NavLink
        to="/admin/app-store-readiness"
        className="inline-flex items-center gap-1.5 hover:text-slate-300 transition-colors"
        title={smokeTitle}
        data-testid="sidebar-smoke-dot"
        data-state={smokeState}
      >
        <ShieldCheck className="w-3 h-3" />
        <span
          className={`inline-block w-2 h-2 rounded-full ${dotColor(
            smoke?.state,
            smokeError,
          )}`}
        />
        <span className="uppercase tracking-wider">smoke</span>
      </NavLink>
      <NavLink
        to="/admin/app-store-readiness"
        className="inline-flex items-center gap-1.5 hover:text-slate-300 transition-colors"
        title={storageTitle}
        data-testid="sidebar-storage-dot"
        data-state={storageState}
      >
        <HardDrive className="w-3 h-3" />
        <span
          className={`inline-block w-2 h-2 rounded-full ${dotColor(
            storage?.state,
            storageError,
          )}`}
        />
        <span className="uppercase tracking-wider">disk</span>
      </NavLink>
    </div>
  );
}
